import { useMemo, useState } from 'react';
import { useQueries, useQuery } from '@tanstack/react-query';
import type { MetricPoint } from '@lizrd/contracts';
import type { LineSeries } from '../ui/line-chart';
import { LineChart } from '../ui/line-chart';
import { Select } from '../ui/select';
import { SkeletonText } from '../ui/skeleton';
import { getDomainHistory, getOrgSavingsSummary } from '../../lib/api';
import { seriesColor } from '../../lib/chart-colors';
import { periodSince } from '../../lib/savings';
import { ExpandableChartShell } from './ExpandableChartShell';

type PeriodKey = '30' | '60' | '90' | 'all';
const PERIOD_OPTIONS = [
  { value: '30' as const, label: 'Last 30 days' },
  { value: '60' as const, label: 'Last 60 days' },
  { value: '90' as const, label: 'Last 90 days' },
  { value: 'all' as const, label: 'All time' },
];
const periodDays = (p: PeriodKey) => (p === 'all' ? null : Number(p));

const withinSince = (points: MetricPoint[], since: Date | null) =>
  since ? points.filter((p) => new Date(p.day) >= since) : points;

const INFO = (
  <>
    <p>
      Each domain&apos;s <b>Utilization Score</b> (1–5, higher is better) over time — one point per
      day. Rising lines mean less idle spend.
    </p>
    <p>Expand to change the window and toggle domains in the legend.</p>
  </>
);

/**
 * Utilization-score timeline: one line per domain on a fixed 1–5 axis, so a
 * director can see at a glance which domains are getting better- or
 * worse-utilized over time. Compact preview expands (click) into a full modal.
 */
export function UtilizationScoreTrend() {
  const summary = useQuery({ queryKey: ['org-savings-summary'], queryFn: getOrgSavingsSummary });
  const list = summary.data?.domains ?? [];
  const histories = useQueries({
    queries: list.map((e) => ({
      queryKey: ['domain-history', e.domainId],
      queryFn: () => getDomainHistory(e.domainId),
      staleTime: 60_000,
    })),
  });
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [period, setPeriod] = useState<PeriodKey>('90');

  const loaded = !!summary.data && histories.every((h) => h.data !== undefined);
  const historiesKey = histories.map((h) => h.dataUpdatedAt).join(',');

  const { series, hasData } = useMemo(() => {
    const since = periodSince(periodDays(period));
    const series: LineSeries[] = list.map((e, i) => ({
      key: e.domainId,
      label: e.name,
      color: seriesColor(i, list.length),
      points: withinSince(histories[i]?.data?.utilizationScore ?? [], since),
    }));
    return { series, hasData: series.some((s) => s.points.length > 0) };
  }, [list, histories, period, historiesKey]);

  const toggle = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  const visible = series.filter((s) => !hidden.has(s.key));

  return (
    <ExpandableChartShell title="Utilization score over time" info={INFO}>
      {(expanded) =>
        !loaded ? (
          <SkeletonText lines={4} lineClassName="h-4" />
        ) : !hasData ? (
          <p className="py-6 text-center text-sm text-muted">
            No utilization history yet — it builds one point per day after each sync.
          </p>
        ) : (
          <>
            {expanded && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-x-3 gap-y-1">
                  {series.map((s) => {
                    const off = hidden.has(s.key);
                    return (
                      <button
                        key={s.key}
                        type="button"
                        onClick={() => toggle(s.key)}
                        className={`flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[0.7rem] transition-colors ${
                          off
                            ? 'border-hairline text-muted line-through'
                            : 'border-hairline text-haze hover:text-bone'
                        }`}
                      >
                        <span
                          className="inline-block h-2 w-2 rounded-full"
                          style={{ backgroundColor: off ? 'var(--muted)' : s.color }}
                        />
                        {s.label}
                      </button>
                    );
                  })}
                </div>
                <Select
                  value={period}
                  onChange={(v) => setPeriod(v as PeriodKey)}
                  ariaLabel="Time window"
                  size="sm"
                  options={PERIOD_OPTIONS}
                />
              </div>
            )}
            <LineChart
              series={expanded ? visible : series}
              height={expanded ? 300 : 150}
              yMax={5}
              yTicks={[1, 2, 3, 4, 5]}
              formatValue={(n) => `${n.toFixed(1)}/5`}
              formatTick={(n) => `${n}`}
            />
          </>
        )
      }
    </ExpandableChartShell>
  );
}
